// Relances par e-mail (Brevo) : réglages, essai, et ce qu'il advient de
// chaque e-mail.
//
// Romain (26/10/2026) : « un devis sur deux n'arrive jamais dans la boîte du
// client ». Les relances partent de contact@imptoiture.com par Brevo, et le
// journal dit pour chacune : délivré, ouvert, rejeté.

import { messageErreur } from '@imp/shared/message-erreur';
import {
  EMAIL_SETTINGS_DEFAULT,
  type EmailSettings,
  LIBELLES_STATUT_EMAIL,
  type StatutEmail,
} from '@imp/shared/relances-email';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Save, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  envoyerEmailTest,
  getEmailSettings,
  listEmailEnvois,
  saveEmailSettings,
} from '@/modules/messagerie/api';

const TEINTE: Record<StatutEmail, string> = {
  en_file: 'bg-muted text-muted-foreground',
  envoye: 'bg-sky-100 text-sky-800',
  differe: 'bg-amber-100 text-amber-800',
  delivre: 'bg-emerald-100 text-emerald-800',
  ouvert: 'bg-emerald-200 text-emerald-900',
  clique: 'bg-emerald-200 text-emerald-900',
  rejete: 'bg-red-100 text-red-800',
  spam: 'bg-red-100 text-red-800',
  echec: 'bg-red-100 text-red-800',
};

function paliersTexte(p: number[]): string {
  return p.join(', ');
}
function lirePaliers(s: string): number[] {
  return [
    ...new Set(
      s
        .split(/[,\s]+/)
        .map((x) => parseInt(x, 10))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ].sort((a, b) => a - b);
}

export function RelancesEmailCard() {
  const qc = useQueryClient();
  const reglagesQ = useQuery({ queryKey: ['email-settings'], queryFn: getEmailSettings });
  const journalQ = useQuery({ queryKey: ['email-envois'], queryFn: () => listEmailEnvois(30) });
  const [s, setS] = useState<EmailSettings>(EMAIL_SETTINGS_DEFAULT);
  const [paliers, setPaliers] = useState('7, 14');
  const [adresseTest, setAdresseTest] = useState('');

  useEffect(() => {
    if (reglagesQ.data) {
      setS(reglagesQ.data);
      setPaliers(paliersTexte(reglagesQ.data.paliers));
    }
  }, [reglagesQ.data]);

  const enregistrer = useMutation({
    mutationFn: () => saveEmailSettings({ ...s, paliers: lirePaliers(paliers) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-settings'] });
    },
  });
  const essai = useMutation({
    mutationFn: () => envoyerEmailTest(adresseTest.trim(), s),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-envois'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" /> Relances des devis par e-mail (Brevo)
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          Les devis envoyés restés sans réponse sont relancés par e-mail, depuis{' '}
          <strong>contact@imptoiture.com</strong>, aux jours choisis après l’envoi. Un devis relancé
          ne l’est qu’une fois par palier ; il sort de la liste « à relancer » pour 7 jours. Rien ne
          part avant 9 h ni après 19 h, ni le dimanche (créneau des SMS ci-dessous).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {reglagesQ.isError ? (
          <p className="text-destructive text-sm">{messageErreur(reglagesQ.error)}</p>
        ) : null}
        <div>
          <label htmlFor="email-actif" className="flex items-center gap-3 font-medium">
            <input
              id="email-actif"
              type="checkbox"
              className="h-4 w-4"
              checked={s.actif}
              onChange={(e) => {
                setS({ ...s, actif: e.target.checked });
              }}
            />
            Activer les relances automatiques par e-mail
          </label>
          <p className="text-muted-foreground ml-7 text-sm">
            Indépendant des SMS : les e-mails peuvent partir même si les SMS sont éteints.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
          <div className="space-y-1">
            <Label htmlFor="email-paliers">Jours après l’envoi</Label>
            <Input
              id="email-paliers"
              value={paliers}
              onChange={(e) => {
                setPaliers(e.target.value);
              }}
              placeholder="7, 14"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="email-objet">Objet</Label>
            <Input
              id="email-objet"
              value={s.objet}
              onChange={(e) => {
                setS({ ...s, objet: e.target.value });
              }}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="email-message">Message</Label>
          <Textarea
            id="email-message"
            rows={12}
            value={s.message}
            onChange={(e) => {
              setS({ ...s, message: e.target.value });
            }}
          />
          <p className="text-muted-foreground text-xs">
            Variables : {'{prenom}'} {'{numero}'} {'{montant}'} {'{date}'} (date d’envoi du devis).
            Les réponses du client arrivent dans la boîte contact@imptoiture.com.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={enregistrer.isPending}
            onClick={() => {
              enregistrer.mutate();
            }}
          >
            <Save className="h-4 w-4" /> Enregistrer les relances e-mail
          </Button>
          {enregistrer.isError ? (
            <span className="text-destructive text-sm">{messageErreur(enregistrer.error)}</span>
          ) : enregistrer.isSuccess ? (
            <span className="text-money-in text-sm">Enregistré.</span>
          ) : null}
        </div>

        {/* ----- Essai ----- */}
        <div className="bg-muted/30 space-y-2 rounded-md border p-3">
          <p className="text-sm font-medium">Essayer avant d’activer</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Adresse de test"
              type="email"
              className="w-72"
              placeholder="votre adresse, ex. imp.toiture@gmail.com"
              value={adresseTest}
              onChange={(e) => {
                setAdresseTest(e.target.value);
              }}
            />
            <Button
              variant="outline"
              disabled={essai.isPending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adresseTest.trim())}
              onClick={() => {
                essai.mutate();
              }}
            >
              <Send className="h-4 w-4" /> {essai.isPending ? 'Envoi…' : 'M’envoyer un essai'}
            </Button>
          </div>
          {essai.isError ? (
            <p className="text-destructive text-sm">{messageErreur(essai.error)}</p>
          ) : essai.isSuccess ? (
            <p className="text-money-in text-sm">
              Parti. Vérifiez qu’il arrive en boîte de réception, pas dans les indésirables.
            </p>
          ) : null}
        </div>

        {/* ----- Journal ----- */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Derniers e-mails envoyés</p>
          {journalQ.isError ? (
            <p className="text-destructive text-sm">{messageErreur(journalQ.error)}</p>
          ) : (journalQ.data ?? []).length === 0 ? (
            <p className="text-muted-foreground text-sm">Aucun e-mail envoyé pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                    <th className="py-1.5 pr-3">Date</th>
                    <th className="py-1.5 pr-3">Destinataire</th>
                    <th className="py-1.5 pr-3">Objet</th>
                    <th className="py-1.5">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {(journalQ.data ?? []).map((e) => (
                    <tr key={e.id} className="border-b last:border-0">
                      <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">
                        {new Date(e.created_at).toLocaleString('fr-FR', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </td>
                      <td className="py-1.5 pr-3">{e.destinataire}</td>
                      <td className="py-1.5 pr-3">
                        {e.entite_type === 'devis' && e.entite_id ? (
                          <Link
                            to={`/devis/${e.entite_id}`}
                            className="text-primary hover:underline"
                          >
                            {e.objet}
                          </Link>
                        ) : (
                          e.objet
                        )}
                      </td>
                      <td className="py-1.5">
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${TEINTE[e.statut]}`}
                          title={e.erreur ?? undefined}
                        >
                          {LIBELLES_STATUT_EMAIL[e.statut]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
